import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { baseUrl } from '../../api/baseUrl';

const PullApi = () => {
    const [loading, setLoading] = useState(true);
    const [response, setResponse] = useState(null);
    const [error, setError] = useState(null);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const makeApiCall = async () => {
            try {
                setLoading(true);

                // Get parameters from URL
                const encodedUserId = searchParams.get('userId');
                const couponCode = searchParams.get('couponCode');
                let clientSecret = searchParams.get('clientSecret') || searchParams.get('clientSECRET_ACCESS_KEY');
                console.log('clientSecret from URL: ', clientSecret);

                // Check if required parameters are present
                if (!encodedUserId || !couponCode) {
                    setError('Missing required parameters: userId and couponCode');
                    setLoading(false);
                    return;
                }

                // Decode the userId (if it's base64 encoded or URL encoded)
                let userId;
                try {
                    userId = decodeURIComponent(encodedUserId);
                } catch (e) {
                    userId = encodedUserId; // Use as is if decoding fails
                }

                // Fetch coupon details to get productId and campaign's clientSecret
                const couponListRes = await axios.get(`${baseUrl}/admin/campaign/list-coupon`, {
                    headers: {
                        'auth-key': 'e99dd7a6250d09484402a061f01d1698bfb30a57bcec3b3f70ecbdd6c8258eb0',
                    }
                });
                const matchedCoupon = couponListRes.data?.data?.find(c => c.code === couponCode);
                const productId = matchedCoupon?.productId?._id || matchedCoupon?.productId || '';

                // Auto-fetch clientSecret from campaign's uniqueId if not provided in URL
                if (!clientSecret && matchedCoupon?.campaignId) {
                    // campaignId is populated as object in list-coupon response
                    if (typeof matchedCoupon.campaignId === 'object' && matchedCoupon.campaignId.uniqueId) {
                        clientSecret = matchedCoupon.campaignId.uniqueId;
                        console.log('clientSecret auto-fetched from coupon campaign data: ', clientSecret);
                    } else {
                        // Fallback: fetch campaign separately
                        const campaignId = typeof matchedCoupon.campaignId === 'object' ? matchedCoupon.campaignId._id : matchedCoupon.campaignId;
                        try {
                            const campaignRes = await axios.get(`${baseUrl}/admin/campaign/view-campaign/${campaignId}`, {
                                headers: {
                                    'auth-key': 'e99dd7a6250d09484402a061f01d1698bfb30a57bcec3b3f70ecbdd6c8258eb0',
                                }
                            });
                            clientSecret = campaignRes.data?.data?.uniqueId || '';
                            console.log('clientSecret auto-fetched from campaign API: ', clientSecret);
                        } catch (e) {
                            console.log('Failed to fetch campaign for clientSecret:', e);
                        }
                    }
                }

                if (!clientSecret) {
                    setError('Could not determine client secret. Please provide clientSecret in URL or check campaign configuration.');
                    setLoading(false);
                    return;
                }

                const data = {
                    "orderId": `order-${Date.now()}`,
                    "couponCode": couponCode,
                    "redemption": new Date(),
                    "userId": userId,
                    "productId": productId
                };

                const config = {
                    method: 'post',
                    maxBodyLength: Infinity,
                    url: `${baseUrl}/api/hooks/pull-api-store`,
                    headers: {
                        'client-secret': clientSecret,
                        'auth-key': 'e99dd7a6250d09484402a061f01d1698bfb30a57bcec3b3f70ecbdd6c8258eb0',
                        'Content-Type': 'application/json'
                    },
                    data: data
                };

                const result = await axios.request(config);
                console.log(JSON.stringify(result.data));
                setResponse(result.data);
            } catch (err) {
                console.log(err);
                const errorMsg = err?.response?.data?.message
                    || err?.response?.data?.error
                    || (err?.response?.data ? JSON.stringify(err.response.data) : null)
                    || `${err.message} | Status: ${err?.response?.status || 'N/A'} | URL: ${baseUrl}/api/hooks/pull-api-store`;
                setError(errorMsg);
            } finally {
                setLoading(false);
            }
        };

        makeApiCall();
    }, []);

    return (
        <div className="h-screen w-full flex items-center justify-center bg-gray-100">
            <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
                <h1 className="text-2xl font-bold text-center mb-6 text-gray-800">
                    Pull API Store
                </h1>

                {/* Show URL parameters */}
                <div className="mb-4 p-3 bg-blue-50 rounded border">
                    <p className="text-sm text-gray-600">
                        <strong>User ID:</strong> {searchParams.get('userId') || 'Not provided'}
                    </p>
                    <p className="text-sm text-gray-600">
                        <strong>Coupon Code:</strong> {searchParams.get('couponCode') || 'Not provided'}
                    </p>
                    <p className="text-sm text-gray-600">
                        <strong>Client Secret:</strong> {searchParams.get('clientSecret') || searchParams.get('clientSECRET_ACCESS_KEY') ? 'Provided' : 'Auto-fetched from campaign'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                        <strong>API URL:</strong> {baseUrl}/api/hooks/pull-api-store
                    </p>
                </div>

                {loading && (
                    <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="mt-2 text-gray-600">Processing API call...</p>
                    </div>
                )}

                {error && (
                    <div className="bg-red-100 border border-red text-red px-4 py-3 rounded">
                        <strong className="font-bold">Error: </strong>
                        <span className="block sm:inline">{error}</span>
                    </div>
                )}

                {response && (
                    <div className="bg-green-100 border border-primary text-primary px-4 py-3 rounded">
                        <strong className="font-bold">Success! </strong>
                        <span className="block sm:inline">API call processed successfully.</span>
                        <pre className="mt-2 text-xs bg-white p-2 rounded border overflow-auto">
                            {JSON.stringify(response, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PullApi;